import React, { useCallback, useEffect, useState } from 'react';
import { Globe, Rocket, BarChart3, RefreshCw, ExternalLink, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import AppLayout from '../../layouts/AppLayout';
import { metaAdsService } from '../../services';
import './styles.css';

function MetaAdsPage() {
  const [metaStatus, setMetaStatus] = useState({ connected: false, loading: true });
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loadingPerf, setLoadingPerf] = useState(false);

  // Launch form
  const [launchForm, setLaunchForm] = useState({
    creative_id: '',
    daily_budget: '5000',
    objective: 'OUTCOME_TRAFFIC',
    destination_url: '',
    headline: '',
    message: '',
    image_url: '',
    target_age_min: '18',
    target_age_max: '65',
    target_country: 'BR'
  });
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);
  const [launchError, setLaunchError] = useState('');

  // Load Meta status
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const data = await metaAdsService.getStatus();
        setMetaStatus({
          connected: Boolean(data?.connected),
          adAccountId: data?.meta_ad_account_id || '',
          pageName: data?.meta_page_name || '',
          loading: false
        });
      } catch {
        setMetaStatus({ connected: false, loading: false });
      }
    };
    loadStatus();
  }, []);

  // Load campaigns
  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const data = await metaAdsService.getCampaigns();
      setCampaigns(data?.campaigns || []);
    } catch {
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  useEffect(() => {
    if (metaStatus.connected) loadCampaigns();
  }, [metaStatus.connected, loadCampaigns]);

  // Connect handler
  const handleConnect = async () => {
    try {
      const data = await metaAdsService.getConnectUrl();
      if (data?.auth_url) window.location.assign(data.auth_url);
    } catch {
      // ignore
    }
  };

  // Launch campaign
  const handleLaunch = async (event) => {
    event.preventDefault();
    setLaunchError('');
    setLaunchResult(null);
    setLaunching(true);

    try {
      const payload = {
        ...launchForm,
        daily_budget: parseInt(launchForm.daily_budget, 10) || 5000,
        target_age_min: parseInt(launchForm.target_age_min, 10) || 18,
        target_age_max: parseInt(launchForm.target_age_max, 10) || 65
      };
      const data = await metaAdsService.launchCampaign(payload);

      if (data?.success) {
        setLaunchResult(data);
        loadCampaigns();
      } else {
        setLaunchError(data?.message || data?.meta_error || 'Falha ao criar campanha');
      }
    } catch {
      setLaunchError('Erro de conexão ao criar campanha');
    } finally {
      setLaunching(false);
    }
  };

  // Load performance
  const loadPerformance = async (campaignId) => {
    setSelectedCampaign(campaignId);
    setLoadingPerf(true);
    setPerformance(null);
    try {
      const data = await metaAdsService.getPerformance(campaignId);
      if (data?.success) {
        setPerformance(data);
      } else {
        setPerformance({ error: data?.message || 'Sem dados disponíveis' });
      }
    } catch {
      setPerformance({ error: 'Erro ao carregar performance' });
    } finally {
      setLoadingPerf(false);
    }
  };

  const onFormChange = (field) => (event) => {
    setLaunchForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  if (metaStatus.loading) {
    return (
      <AppLayout title="Meta Ads" subtitle="Campanhas e performance" fullWidth>
        <div className="meta-ads-loading">
          <Loader2 size={32} className="spin-icon" />
          <p>Carregando...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Meta Ads" subtitle="Campanhas Facebook & Instagram" fullWidth>
      <div className="meta-ads-page">

        {/* Connection Status */}
        <section className="meta-ads-card card">
          <h3><Globe size={16} /> Conexão Meta Ads</h3>
          {metaStatus.connected ? (
            <div className="meta-ads-connected">
              <CheckCircle2 size={18} className="icon-success" />
              <div>
                <p>Conectado</p>
                {metaStatus.adAccountId && <span className="meta-ads-detail">Ad Account: {metaStatus.adAccountId}</span>}
                {metaStatus.pageName && <span className="meta-ads-detail">Página: {metaStatus.pageName}</span>}
              </div>
            </div>
          ) : (
            <div className="meta-ads-disconnected">
              <p>Conecte sua conta Meta para criar campanhas no Facebook e Instagram.</p>
              <button className="btn btn-outline" type="button" onClick={handleConnect}>
                <div className="blob1"></div>
                <div className="blob2"></div>
                <span className="inner"><Globe size={16} /> Conectar Meta Ads</span>
              </button>
            </div>
          )}
        </section>

        {metaStatus.connected && (
          <>
            {/* Launch Campaign Form */}
            <section className="meta-ads-card card">
              <h3><Rocket size={16} /> Lançar Campanha</h3>

              {launchResult && (
                <div className="meta-ads-success">
                  <CheckCircle2 size={16} />
                  <span>Campanha criada com sucesso! ID: {launchResult.meta_campaign_id}</span>
                </div>
              )}

              {launchError && (
                <div className="meta-ads-error">
                  <AlertCircle size={16} />
                  <span>{launchError}</span>
                </div>
              )}

              <form className="meta-ads-form" onSubmit={handleLaunch}>
                <div className="meta-ads-form-grid">
                  <div className="input-group">
                    <label>Objetivo</label>
                    <select value={launchForm.objective} onChange={onFormChange('objective')}>
                      <option value="OUTCOME_TRAFFIC">Tráfego</option>
                      <option value="OUTCOME_ENGAGEMENT">Engajamento</option>
                      <option value="OUTCOME_LEADS">Leads</option>
                      <option value="OUTCOME_SALES">Vendas</option>
                      <option value="OUTCOME_AWARENESS">Reconhecimento</option>
                    </select>
                  </div>

                  <div className="input-group">
                    <label>Orçamento diário (R$)</label>
                    <input
                      type="number"
                      min="500"
                      step="100"
                      value={launchForm.daily_budget}
                      onChange={onFormChange('daily_budget')}
                      placeholder="5000 = R$ 50,00"
                      required
                    />
                    <small>Em centavos — 5000 = R$ 50,00</small>
                  </div>

                  <div className="input-group">
                    <label>URL de destino</label>
                    <input
                      type="url"
                      value={launchForm.destination_url}
                      onChange={onFormChange('destination_url')}
                      placeholder="https://suaempresa.com.br/oferta"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label>Título do anúncio</label>
                    <input
                      value={launchForm.headline}
                      onChange={onFormChange('headline')}
                      placeholder="Oferta imperdível..."
                      required
                    />
                  </div>

                  <div className="input-group full-width">
                    <label>Mensagem do anúncio</label>
                    <textarea
                      rows={3}
                      value={launchForm.message}
                      onChange={onFormChange('message')}
                      placeholder="Texto principal que aparecerá no feed..."
                      required
                    />
                  </div>

                  <div className="input-group full-width">
                    <label>URL da imagem</label>
                    <input
                      type="url"
                      value={launchForm.image_url}
                      onChange={onFormChange('image_url')}
                      placeholder="https://exemplo.com/imagem.jpg"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label>Idade mínima</label>
                    <input type="number" min="13" max="65" value={launchForm.target_age_min} onChange={onFormChange('target_age_min')} />
                  </div>

                  <div className="input-group">
                    <label>Idade máxima</label>
                    <input type="number" min="13" max="65" value={launchForm.target_age_max} onChange={onFormChange('target_age_max')} />
                  </div>

                  <div className="input-group">
                    <label>País alvo</label>
                    <input value={launchForm.target_country} onChange={onFormChange('target_country')} placeholder="BR" />
                  </div>

                  <div className="input-group">
                    <label>Creative ID (opcional)</label>
                    <input value={launchForm.creative_id} onChange={onFormChange('creative_id')} placeholder="ID do criativo MP" />
                  </div>
                </div>

                <button className="btn btn-outline" type="submit" disabled={launching}>
                  <div className="blob1"></div>
                  <div className="blob2"></div>
                  <span className="inner">
                    {launching ? <><Loader2 size={16} className="spin-icon" /> Criando campanha...</> : <><Rocket size={16} /> Lançar Campanha</>}
                  </span>
                </button>
              </form>
            </section>

            {/* Campaigns List */}
            <section className="meta-ads-card card">
              <div className="meta-ads-card-header">
                <h3><BarChart3 size={16} /> Campanhas</h3>
                <button className="btn-icon" type="button" onClick={loadCampaigns} disabled={loadingCampaigns}>
                  <RefreshCw size={16} className={loadingCampaigns ? 'spin-icon' : ''} />
                </button>
              </div>

              {campaigns.length === 0 && !loadingCampaigns && (
                <p className="meta-ads-empty">Nenhuma campanha Meta criada ainda.</p>
              )}

              {campaigns.length > 0 && (
                <div className="meta-ads-campaigns-list">
                  {campaigns.map((c) => (
                    <div key={c.id || c._id} className={`meta-ads-campaign-row ${selectedCampaign === c.id ? 'is-selected' : ''}`}>
                      <div className="meta-ads-campaign-info">
                        <strong>{c.headline || c.objective || 'Campanha'}</strong>
                        <span className="meta-ads-campaign-meta">
                          {c.objective} &middot; R$ {((c.daily_budget || 0) / 100).toFixed(2)}/dia
                        </span>
                        <span className="meta-ads-campaign-meta">
                          Status: {c.status || 'PAUSED'} &middot; {c.created_at || '---'}
                        </span>
                      </div>
                      <div className="meta-ads-campaign-actions">
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => loadPerformance(c.id)}>
                          <BarChart3 size={14} /> Performance
                        </button>
                        {c.destination_url && (
                          <a href={c.destination_url} target="_blank" rel="noopener noreferrer" className="btn-icon">
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Performance Panel */}
              {selectedCampaign && (
                <div className="meta-ads-performance">
                  <h4>Performance da Campanha</h4>
                  {loadingPerf && (
                    <div className="meta-ads-loading-inline">
                      <Loader2 size={20} className="spin-icon" />
                      <span>Carregando métricas...</span>
                    </div>
                  )}

                  {performance?.error && (
                    <div className="meta-ads-error">
                      <AlertCircle size={16} />
                      <span>{performance.error}</span>
                    </div>
                  )}

                  {performance && !performance.error && !loadingPerf && (
                    <div className="meta-ads-metrics-grid">
                      <div className="meta-ads-metric">
                        <span className="meta-ads-metric-value">{performance.impressions?.toLocaleString('pt-BR') || '0'}</span>
                        <span className="meta-ads-metric-label">Impressões</span>
                      </div>
                      <div className="meta-ads-metric">
                        <span className="meta-ads-metric-value">{performance.clicks?.toLocaleString('pt-BR') || '0'}</span>
                        <span className="meta-ads-metric-label">Cliques</span>
                      </div>
                      <div className="meta-ads-metric">
                        <span className="meta-ads-metric-value">R$ {performance.spend || '0.00'}</span>
                        <span className="meta-ads-metric-label">Gasto</span>
                      </div>
                      <div className="meta-ads-metric">
                        <span className="meta-ads-metric-value">{performance.ctr || '0.00'}%</span>
                        <span className="meta-ads-metric-label">CTR</span>
                      </div>
                      <div className="meta-ads-metric">
                        <span className="meta-ads-metric-value">R$ {performance.cpc || '0.00'}</span>
                        <span className="meta-ads-metric-label">CPC</span>
                      </div>
                      <div className="meta-ads-metric">
                        <span className="meta-ads-metric-value">{performance.conversions?.toLocaleString('pt-BR') || '0'}</span>
                        <span className="meta-ads-metric-label">Conversões</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </AppLayout>
  );
}

export default MetaAdsPage;
